import {
  eligibleEvaluation,
  getRun,
  getVersionIdentity,
  listBlockResponses,
  listCriterionResponses,
  listInstrumentBlocks,
  listInstrumentCriteria,
  listOrganisations,
  listScaleAnchors,
} from "./api";
import { buildPmoReportModel, type PmoReportModel } from "./pmoReport";
import { isSubmitted } from "./respondent";
import type { Run } from "./types";

// Loads one PMO self-assessment's report model for a run, under the caller's
// own row-level security. Shared by the respondent's report (/respond/report),
// the client report the owner prints to PDF, and the assessment dashboard, so
// the three read the same frozen evaluation through the same pure builder.
//
// A5: nothing about the respondent — no name, email or id — enters the model;
// the identity carries the organisation, the instrument and the dates only.

export type PmoReportLoad =
  | { kind: "not-submitted"; run: Run; answered: number; total: number }
  | { kind: "ready"; run: Run; model: PmoReportModel; scale: { min: number; max: number } };

export async function loadPmoReport(runId: string): Promise<PmoReportLoad | null> {
  const run = await getRun(runId);
  if (!run) return null;
  const v = run.instrument_version_id;

  if (!isSubmitted(run.state)) {
    const [criteria, critRows] = await Promise.all([listInstrumentCriteria(v), listCriterionResponses(runId)]);
    const answered = critRows.filter((r) => r.applicability !== "not_assessed").length;
    return { kind: "not-submitted", run, answered, total: criteria.length };
  }

  const [evaluation, scales, orgs, version, blocks, criteria, blockRows, critRows] = await Promise.all([
    eligibleEvaluation(run.id),
    listScaleAnchors(v),
    listOrganisations(),
    getVersionIdentity(v),
    listInstrumentBlocks(v),
    listInstrumentCriteria(v),
    listBlockResponses(run.id),
    listCriterionResponses(run.id),
  ]);
  if (!evaluation) throw new Error("no finalized result is recorded for this assessment");
  if (scales.size !== 1) throw new Error("the instrument does not declare exactly one scale");
  const levels = [...scales.values()][0] ?? [];

  const blockKeyOf = new Map(blocks.map((b) => [b.id, b.block_key]));
  const practices = criteria.map((c) => ({
    blockId: blockKeyOf.get(c.instrument_block_id) ?? "",
    criterionId: c.criterion_key,
    sequence: c.sequence,
    title: c.perspective ?? c.criterion_key,
  }));
  // The respondent's OWN justifications — client data, their own words
  // (DISCLOSURE_POLICY, Schedule A as amended for template 1.1.0).
  const blockKeyOfResponse = new Map(blockRows.map((r) => [r.id, r.block_id]));
  const areas = new Map<string, string>();
  for (const r of blockRows) {
    if (r.applicability === "not_applicable" && r.rationale) areas.set(r.block_id, r.rationale);
  }
  const practiceReasons = new Map<string, string>();
  for (const r of critRows) {
    const bk = blockKeyOfResponse.get(r.block_response_id);
    // The N/A reason, and — since DESIGN_SLICE_ANSWER_REASON 291026 — the
    // reason beside a non-full answer, both in the respondent's own words.
    if (bk && (r.applicability === "not_applicable" || r.applicability === "assessed") && r.rationale) {
      practiceReasons.set(`${bk}\u0000${r.criterion_id}`, r.rationale);
    }
  }

  const model = buildPmoReportModel({
    snapshot: evaluation.result,
    levels,
    identity: {
      customer: orgs.find((o) => o.id === run.organisation_id)?.name ?? "",
      assessedOn: evaluation.evaluated_at,
      instrumentTitle: version.title,
      instrumentVersion: version.version,
      definitionHash: version.definition_hash,
    },
    practices,
    justifications: { areas, practices: practiceReasons },
  });
  // The declared scale's bounds, derived from the levels, never a range
  // literal (handover §0; Architect #2's review of the console release).
  const values = levels.map((l) => l.value);
  return { kind: "ready", run, model, scale: { min: Math.min(...values), max: Math.max(...values) } };
}
